import { Tabs, TabsContent, TabsList, TabsTrigger } from '@neo/primitives/tabs'
import { Tooltip } from '@neo/primitives/tooltip'
import { pluralize } from '@shared/utils'
import { isEmpty } from 'lodash'
import { Eye } from 'lucide-react'
import type { ReactNode } from 'react'
import { FlowNodeTypes } from 'v2/pages/flow/types'
import { CollapsiblePrompt } from 'v2/shared/components/collapsible-prompt'
import { FormattedMessage } from 'v2/shared/components/formatted-message'
import { useLivekitEnabled } from 'v2/shared/hooks/use-livekit-enabled'
import { ChosenPath } from '../components/chosen-path'
import { NodeExecutionDetails } from '../components/node-execution-details'
import { NodeName } from '../components/node-name'
import { TaskProgress } from '../components/task-progress'
import {
  getPromptText,
  isTextAgentChannel,
  type NodeChunkCreatedEvent,
  type NodeConfig,
  type NodeExecutedEvent,
} from '../types'
import { UserMessage } from './components/user-message'
import { ExtractedVariablesSummary } from './extracted-variables-summary'
import { AIResponseHeader } from './header'
import { KbMetrics } from './kb-metrics'
import { SkillResultAnalysis } from './skill-result'
import { ToolCallAnalysis } from './tool-call'
import { parseVariables } from './utils'

interface AINodeAnalysisProps {
  node: NodeExecutedEvent
}

const AIResponse = ({ node, chunk }: { node: NodeExecutedEvent; chunk: NodeChunkCreatedEvent }) => {
  const nodeConfig = (node.node_config || {}) as NodeConfig
  const valueMap = new Map<string | ReactNode, string | ReactNode>()

  // Core node information
  valueMap.set(
    'Current node',
    <NodeName name={node.current_node} type={node.current_node_type} key="current-node" />,
  )

  if (node.chosen_path) {
    valueMap.set('Chosen path', <ChosenPath node={node} />)
  }

  valueMap.set(
    'Previous node',
    <NodeName name={node.previous_node} type={node.previous_node_type} key="previous-node" />,
  )

  const promptText = getPromptText(nodeConfig.prompt)
  if (promptText) {
    valueMap.set(
      'Prompt',
      <CollapsiblePrompt text={promptText} className="text-s-400 text-secondary" />,
    )
  }
  if (chunk.text) {
    valueMap.set(
      <span className="text-s-500 text-primary" key="assistant-response-key">
        Assistant response
      </span>,
      <div className="font-inter text-s-400 text-primary" key="assistant-response-value">
        <FormattedMessage
          content={chunk.text}
          markdown
          disallowedElements={[]}
          className="text-s-400 text-primary"
        />
      </div>,
    )
  }

  // Extracted variables. When the node carries an aggregate (LiveKit — vars are
  // extracted async at handoff and land on a dropped empty chunk, so per-response
  // chunks hold none), show them once at the node level instead (see
  // AINodeAnalysis) rather than a misleading "None" on every response. Pipecat
  // carries vars on the response chunk itself, so it keeps the per-response row.
  // Chat only: the node-level summary below is chat-gated, so only chat may drop
  // the per-response row in favour of it. Every other channel keeps today's row.
  const usesNodeLevelSummary = isTextAgentChannel(nodeConfig.channel) && !!node.aggregated_variables
  if (!usesNodeLevelSummary && (nodeConfig.variables || chunk.node_config?.variables)) {
    const variables = nodeConfig.variables || chunk.node_config?.variables || {}
    const variablesArray = parseVariables(variables)

    const variableLength = variablesArray.length
    valueMap.set(
      'Extracted variables',
      variableLength ? (
        <div className="flex items-center gap-1 rounded-md py-1" key="extracted-variables">
          <span className="text-xs-400 text-foreground">
            {pluralize(variableLength, 'Variable')}
          </span>
          <Tooltip
            className="max-h-80 max-w-[min(32rem,calc(100vw-2rem))] overflow-y-auto border border-border bg-popover p-3 text-popover-foreground"
            // @ts-expect-error tooltip accepts react node
            content={
              (
                <div className="flex flex-col gap-2 font-jetbrains text-xs-400 break-words [overflow-wrap:anywhere]">
                  {variablesArray.map((variable) => (
                    <div key={variable.variable_name}>
                      {variable.variable_name}: {variable.variable_value}
                    </div>
                  ))}
                </div>
              ) as React.ReactNode
            }
          >
            <button
              type="button"
              aria-label="View extracted variables"
              className="inline-flex size-6 shrink-0 items-center justify-center rounded text-secondary hover:bg-accent hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="button-view-variables"
            >
              <Eye size={16} aria-hidden />
            </button>
          </Tooltip>
        </div>
      ) : (
        <div className="text-xs-400 text-foreground">None</div>
      ),
    )
  }
  const kbReferences = chunk.metrics?.kb_metrics?.references || []
  const kbChunks = kbReferences.flatMap((ref) => ref?.chunks || [])
  const kbRetrievalTime = kbReferences.reduce((total, ref) => total + (ref?.time || 0), 0)

  if (kbChunks?.length) {
    valueMap.set(
      'Knowledge base retrival time',
      <span className="font-jetbrains text-xs-400 text-primary tabular-nums">
        {typeof kbRetrievalTime === 'number' ? `${kbRetrievalTime.toFixed(2)}s` : 'N/A'}
      </span>,
    )
    valueMap.set(
      'Knowledge base chunks',
      <span className="flex items-center gap-1 text-xs-400 text-primary">
        {pluralize(kbChunks.length, 'Chunk')}
        <KbMetrics chunks={kbChunks} />
      </span>,
    )
  }

  const llmMetrics = chunk.metrics?.llm_metrics

  const metricsMap = new Map<string, string | number>([
    ['Total tokens', llmMetrics?.total_tokens || 'N/A'],
    ['Response time', llmMetrics?.time ? `${llmMetrics.time.toFixed(2)}s` : 'N/A'],
  ])

  return (
    <div className="flex flex-col gap-2">
      <AIResponseHeader node={node} chunkTimestamp={chunk.timestamp} />
      <Tabs defaultValue="node-config">
        <TabsList variant="simple" className="mb-3">
          <TabsTrigger
            value="node-config"
            data-testid="tab-node-execution"
            variant="simple"
            className="flex h-9 flex-1 justify-center text-s-500 px-2 py-2"
          >
            Node execution
          </TabsTrigger>
          <TabsTrigger
            value="llm-metrics"
            data-testid="tab-llm-metrics"
            variant="simple"
            className="flex h-9 flex-1 justify-center text-s-500 px-2 py-2"
          >
            LLM metrics
          </TabsTrigger>
        </TabsList>
        <TabsContent value="node-config">
          <div className="border-t border-border px-2 py-3">
            <div className="flex flex-col gap-2">
              {Array.from(valueMap.entries()).map(([key, value], index) => {
                if (!value) return null
                return (
                  <div
                    key={index}
                    className={
                      key === 'Prompt' ? 'flex flex-col gap-1.5' : 'flex items-start gap-2'
                    }
                  >
                    <div className="text-s-400 text-secondary w-24 shrink-0">{key}:</div>
                    <div className="text-s-400 text-primary min-w-0 flex-1 break-words">
                      {value}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </TabsContent>
        <TabsContent value="llm-metrics">
          <div className="border-t border-border px-2 py-3">
            <div className="flex flex-col gap-2">
              {Array.from(metricsMap.entries()).map(([key, value], index) => (
                <div key={index} className="flex items-start gap-2">
                  <div className="text-s-400 text-secondary w-24 shrink-0">{key}:</div>
                  <div className="font-jetbrains text-xs-400 text-primary tabular-nums min-w-0 flex-1">
                    {value}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}

export const AINodeAnalysis = ({ node }: AINodeAnalysisProps) => {
  const { isLivekitEnabled } = useLivekitEnabled()

  const getChunk = (chunk: NodeChunkCreatedEvent) => {
    if (chunk.sender === 'user') {
      const files =
        chunk.attributes && 'files' in chunk.attributes ? chunk.attributes.files : undefined
      return <UserMessage text={chunk.text} files={files} />
    }

    const hasToolMetrics = !isEmpty(chunk.metrics?.tool_metrics)
    const hasText = !!chunk.text

    if (isLivekitEnabled && hasToolMetrics && hasText) {
      return (
        <div className="flex flex-col gap-3">
          <AIResponse node={node} chunk={chunk} />
          <ToolCallAnalysis node={node} chunk={chunk} hideNodeContext />
        </div>
      )
    }

    if (hasToolMetrics) return <ToolCallAnalysis node={node} chunk={chunk} />
    return <AIResponse node={node} chunk={chunk} />
  }
  return (
    <div className="flex flex-col gap-2">
      <TaskProgress node={node} />
      {node.current_node_type === FlowNodeTypes.ai_skills && (
        <div className="rounded-lg border border-border bg-card p-3">
          <SkillResultAnalysis node={node} />
        </div>
      )}
      {node.chunks?.map((chunk) => {
        if (chunk.event_name !== 'NODE_CHUNK_CREATED') return null
        return (
          <div key={chunk.timestamp} className="rounded-lg border border-border bg-card p-3">
            {getChunk(chunk)}
          </div>
        )
      })}
      {/* Chat only: voice, SMS and WhatsApp keep the per-response row above and
          gain no node-level box, so their DOM is unchanged. */}
      {isTextAgentChannel(node.node_config?.channel) && node.aggregated_variables ? (
        <div className="rounded-lg border border-border bg-card p-3">
          <ExtractedVariablesSummary variables={node.aggregated_variables} />
        </div>
      ) : null}
      <NodeExecutionDetails node={node} />
    </div>
  )
}
